import React, { useState, useEffect } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
    Sparkles,
    LayoutDashboard,
    Inbox,
    CalendarDays,
    History,
    LayoutTemplate,
    HeartHandshake,
    Settings2,
    Menu as MenuIcon,
    X,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react'
import styles from './CSMShell.module.css'

const NAV_ITEMS = [
    { to: '/special-moments', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/special-moments/inbox', label: 'Celebration Inbox', icon: Inbox },
    { to: '/special-moments/calendar', label: 'Calendar', icon: CalendarDays },
    { to: '/special-moments/history', label: 'History', icon: History },
    // { to: '/special-moments/templates', label: 'Templates', icon: LayoutTemplate },
    { to: '/special-moments/insights', label: 'Relationship Insights', icon: HeartHandshake },
    { to: '/special-moments/settings', label: 'Settings', icon: Settings2 },
]

const CSMShell = () => {
    const location = useLocation()
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem('sm_sidebar_collapsed') === '1'
        } catch {
            return false
        }
    })
    const [mobileNavOpen, setMobileNavOpen] = useState(false)

    useEffect(() => {
        try {
            localStorage.setItem('sm_sidebar_collapsed', collapsed ? '1' : '0')
        } catch {
            // localStorage unavailable — ignore silently
        }
    }, [collapsed])

    useEffect(() => {
        setMobileNavOpen(false)
    }, [location.pathname])

    return (
        <div className={styles.shell}>
            <aside
                className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}
                aria-label="Special Moments navigation"
            >
                <div className={styles.sidebarHeader}>
                    {!collapsed && (
                        <span className={styles.sidebarHeaderLabel}>
                            <Sparkles size={15} className={styles.sparkleIcon} />
                            Special Moments
                        </span>
                    )}
                    <button
                        type="button"
                        className={styles.collapseBtn}
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                    </button>
                </div>

                <nav className={styles.nav}>
                    <ul className={styles.navList}>
                        {NAV_ITEMS.map((item) => {
                            const Icon = item.icon
                            return (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        end={item.end}
                                        className={({ isActive }) =>
                                            `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                        }
                                        title={collapsed ? item.label : undefined}
                                    >
                                        <Icon size={16} className={styles.navItemIcon} strokeWidth={1.75} />
                                        {!collapsed && <span className={styles.navItemLabel}>{item.label}</span>}
                                    </NavLink>
                                </li>
                            )
                        })}
                    </ul>
                </nav>
            </aside>

            {mobileNavOpen && (
                <>
                    <button
                        type="button"
                        className={styles.mobileOverlay}
                        onClick={() => setMobileNavOpen(false)}
                        aria-label="Close navigation"
                    />
                    <aside className={styles.mobileDrawer} aria-label="Special Moments navigation">
                        <div className={styles.mobileDrawerHeader}>
                            <span className={styles.sidebarHeaderLabel}>
                                <Sparkles size={15} className={styles.sparkleIcon} />
                                Special Moments
                            </span>
                            <button
                                type="button"
                                className={styles.collapseBtn}
                                onClick={() => setMobileNavOpen(false)}
                                aria-label="Close navigation"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <nav className={styles.nav}>
                            <ul className={styles.navList}>
                                {NAV_ITEMS.map((item) => {
                                    const Icon = item.icon
                                    return (
                                        <li key={item.to}>
                                            <NavLink
                                                to={item.to}
                                                end={item.end}
                                                className={({ isActive }) =>
                                                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                                }
                                            >
                                                <Icon size={16} className={styles.navItemIcon} strokeWidth={1.75} />
                                                <span className={styles.navItemLabel}>{item.label}</span>
                                            </NavLink>
                                        </li>
                                    )
                                })}
                            </ul>
                        </nav>
                    </aside>
                </>
            )}

            <div className={styles.main}>
                <header className={styles.topBar}>
                    <span className={styles.topBarTitle}>
                        <Sparkles size={15} className={styles.sparkleIcon} />
                        Special Moments
                    </span>
                    <button
                        type="button"
                        className={styles.mobileMenuBtn}
                        onClick={() => setMobileNavOpen(true)}
                        aria-label="Open navigation"
                    >
                        <MenuIcon size={18} />
                    </button>
                </header>

                <main className={styles.pageOutlet}>
                    <Outlet />
                </main>
            </div>
        </div>
    )
}

export default CSMShell
